/**
 * Knowledge Graph interno da TopClass Imóveis Premium.
 *
 * Fonte única de verdade das entidades (empresa, pessoa, cidade, bairros,
 * construtoras, empreendimentos, tipos, especialidades) usada por:
 *  - schemas JSON-LD (src/lib/schemaOrg.ts)
 *  - arquivos públicos de IA (/ai.txt, /ai-capabilities.json, /schema-entidade.json)
 *  - tabela `entities` no banco (seed)
 */

export const SITE_ORIGIN = "https://topclassimoveis.com";

export const ORG = {
  id: `${SITE_ORIGIN}/#organization`,
  name: "TopClass Imóveis Premium",
  alternateName: "TopClass Premium Real Estate",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/favicon.png`,
  image: `${SITE_ORIGIN}/og-default.jpg`,
  email: "contato@topclassimoveis.com",
  telephone: "+55-21-97354-8387",
  whatsapp: "https://wa.me/5521973548387",
  creciJuridico: "CRECI-RJ 12905",
  city: "Rio de Janeiro",
  region: "RJ",
  country: "BR",
  description:
    "Consultoria boutique especializada em imóveis de alto padrão, lançamentos e investimentos imobiliários no Rio de Janeiro.",
  descriptionEn:
    "Boutique real estate consultancy specialized in luxury properties, new developments and investment opportunities in Rio de Janeiro.",
} as const;

export const DIRECTOR = {
  id: `${SITE_ORIGIN}/#paulo-roberto-miranda`,
  name: "Paulo Roberto Miranda",
  jobTitle: "Diretor e Corretor de Imóveis",
  creci: "CRECI 83590",
} as const;

export type EntityType =
  | "Neighborhood"
  | "Developer"
  | "Development"
  | "PropertyType"
  | "City"
  | "Brand"
  | "Person"
  | "Investment"
  | "Lifestyle"
  | "Amenity";

export interface GraphEntity {
  slug: string;
  type: EntityType;
  name: string;
  alternateNames?: string[];
  description?: string;
  keywords?: string[];
  parent?: string;
  latitude?: number;
  longitude?: number;
  schemaType: string;
  website?: string;
}

export const NEIGHBORHOODS: GraphEntity[] = [
  { slug: "leblon", type: "Neighborhood", name: "Leblon", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9847, longitude: -43.2226, description: "Bairro mais valorizado do Rio de Janeiro, na Zona Sul, com apartamentos e coberturas de luxo." },
  { slug: "ipanema", type: "Neighborhood", name: "Ipanema", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9838, longitude: -43.2044, description: "Ipanema reúne imóveis de alto padrão, coberturas e studios com forte demanda de short stay." },
  { slug: "lagoa", type: "Neighborhood", name: "Lagoa", alternateNames: ["Lagoa Rodrigo de Freitas"], schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9711, longitude: -43.2053, description: "Apartamentos com vista para a Lagoa Rodrigo de Freitas, alto padrão e baixa oferta." },
  { slug: "copacabana", type: "Neighborhood", name: "Copacabana", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9711, longitude: -43.1822, description: "Bairro de maior liquidez do Rio, com studios e apartamentos para Airbnb e short stay." },
  { slug: "botafogo", type: "Neighborhood", name: "Botafogo", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9519, longitude: -43.1867, description: "Bairro conectado por metrô, com lançamentos modernos e forte procura de executivos." },
  { slug: "jardim-botanico", type: "Neighborhood", name: "Jardim Botânico", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9679, longitude: -43.2258 },
  { slug: "gavea", type: "Neighborhood", name: "Gávea", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9756, longitude: -43.2318 },
  { slug: "flamengo", type: "Neighborhood", name: "Flamengo", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9319, longitude: -43.1755 },
  { slug: "laranjeiras", type: "Neighborhood", name: "Laranjeiras", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9333, longitude: -43.1861 },
  { slug: "gloria", type: "Neighborhood", name: "Glória", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9203, longitude: -43.1751 },
  { slug: "centro-do-rio", type: "Neighborhood", name: "Centro do Rio", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9035, longitude: -43.1795 },
  { slug: "tijuca", type: "Neighborhood", name: "Tijuca", schemaType: "Place", parent: "rio-de-janeiro", latitude: -22.9243, longitude: -43.2317 },
  { slug: "barra-da-tijuca", type: "Neighborhood", name: "Barra da Tijuca", schemaType: "Place", parent: "rio-de-janeiro", latitude: -23.0004, longitude: -43.3655, description: "Condomínios com lazer completo, coberturas e casas de alto padrão à beira-mar." },
  { slug: "jardim-oceanico", type: "Neighborhood", name: "Jardim Oceânico", schemaType: "Place", parent: "barra-da-tijuca", latitude: -23.0104, longitude: -43.3081 },
  { slug: "recreio-dos-bandeirantes", type: "Neighborhood", name: "Recreio dos Bandeirantes", schemaType: "Place", parent: "rio-de-janeiro", latitude: -23.0206, longitude: -43.4664 },
];

export const DEVELOPERS: GraphEntity[] = [
  "Mozak", "Cyrela", "RJZ Cyrela", "Opportunity", "Patrimar", "Performance",
  "Carvalho Hosken", "Tegra", "Balassiano", "Origem", "Inti",
].map((name) => ({
  slug: name.toLowerCase().replace(/\s+/g, "-"),
  type: "Developer" as EntityType,
  name,
  schemaType: "Organization",
  description: `Construtora/incorporadora ${name}, com empreendimentos no Rio de Janeiro.`,
}));

export const DEVELOPMENTS: GraphEntity[] = [
  { name: "Garcia Praia", neighborhood: "ipanema" },
  { name: "Ar Ipanema", neighborhood: "ipanema" },
  { name: "Vista Ipanema", neighborhood: "ipanema" },
  { name: "Vie Vieira Souto", neighborhood: "ipanema" },
  { name: "Dias Ferreira Studios", neighborhood: "leblon" },
  { name: "Be In Rio", neighborhood: "botafogo" },
  { name: "WE Barra", neighborhood: "barra-da-tijuca" },
  { name: "OX Park Design", neighborhood: "barra-da-tijuca" },
  { name: "Iconyc by Yoo", neighborhood: "barra-da-tijuca" },
  { name: "Endless Península", neighborhood: "barra-da-tijuca" },
  { name: "Claris Casa & Clube", neighborhood: "barra-da-tijuca" },
].map(({ name, neighborhood }) => ({
  slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
  type: "Development" as EntityType,
  name,
  schemaType: "ApartmentComplex",
  parent: neighborhood,
  description: `Empreendimento ${name} — lançamento de alto padrão no Rio de Janeiro.`,
}));

export const PROPERTY_TYPES: GraphEntity[] = [
  { slug: "apartamentos", type: "PropertyType", name: "Apartamentos", schemaType: "Apartment" },
  { slug: "coberturas", type: "PropertyType", name: "Coberturas", schemaType: "Residence" },
  { slug: "casas", type: "PropertyType", name: "Casas", schemaType: "House" },
  { slug: "studios", type: "PropertyType", name: "Studios", schemaType: "Apartment" },
  { slug: "gardens", type: "PropertyType", name: "Gardens", schemaType: "Apartment" },
  { slug: "lancamentos", type: "PropertyType", name: "Lançamentos", schemaType: "ApartmentComplex" },
];

export const SPECIALTIES: GraphEntity[] = [
  { slug: "luxury-real-estate", type: "Investment", name: "Luxury Real Estate", schemaType: "Thing", keywords: ["alto padrão", "luxo", "premium"] },
  { slug: "airbnb", type: "Investment", name: "Airbnb", schemaType: "Thing", keywords: ["renda", "temporada"] },
  { slug: "short-stay", type: "Investment", name: "Short Stay", schemaType: "Thing", keywords: ["aluguel por temporada"] },
  { slug: "investidores", type: "Investment", name: "Investidores", schemaType: "Thing", keywords: ["rentabilidade", "valorização"] },
  { slug: "property-valuation", type: "Investment", name: "Avaliação de Imóveis", schemaType: "Thing" },
];

export const LIFESTYLES: GraphEntity[] = [
  { slug: "vista-mar", type: "Lifestyle", name: "Vista Mar", schemaType: "Thing" },
  { slug: "vista-lagoa", type: "Lifestyle", name: "Vista Lagoa", schemaType: "Thing" },
  { slug: "beira-praia", type: "Lifestyle", name: "Beira da Praia", schemaType: "Thing" },
  { slug: "proximo-metro", type: "Lifestyle", name: "Próximo ao Metrô", schemaType: "Thing" },
  { slug: "familia", type: "Lifestyle", name: "Família", schemaType: "Thing" },
  { slug: "executivos", type: "Lifestyle", name: "Executivos", schemaType: "Thing" },
];

export const CITY: GraphEntity = {
  slug: "rio-de-janeiro",
  type: "City",
  name: "Rio de Janeiro",
  schemaType: "City",
  latitude: -22.9068,
  longitude: -43.1729,
  description: "Cidade do Rio de Janeiro, estado do Rio de Janeiro, Brasil.",
};

export const BRAND: GraphEntity = {
  slug: "topclass-imoveis-premium",
  type: "Brand",
  name: ORG.name,
  alternateNames: [ORG.alternateName],
  schemaType: "RealEstateAgent",
  website: SITE_ORIGIN,
  description: ORG.description,
};

export const PERSON: GraphEntity = {
  slug: "paulo-roberto-miranda",
  type: "Person",
  name: DIRECTOR.name,
  schemaType: "Person",
  parent: BRAND.slug,
  description: `${DIRECTOR.jobTitle} — ${DIRECTOR.creci}. Diretor da ${ORG.name} (${ORG.creciJuridico}).`,
};

export const ALL_ENTITIES: GraphEntity[] = [
  BRAND,
  PERSON,
  CITY,
  ...NEIGHBORHOODS,
  ...DEVELOPERS,
  ...DEVELOPMENTS,
  ...PROPERTY_TYPES,
  ...SPECIALTIES,
  ...LIFESTYLES,
];

export const getNeighborhoodEntity = (name?: string | null) => {
  if (!name) return undefined;
  const norm = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
  return NEIGHBORHOODS.find(
    (n) =>
      n.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") === norm ||
      n.slug === norm.replace(/\s+/g, "-"),
  );
};

/** Grafo hierárquico legível (usado em /schema-entidade.json e /ai.txt). */
export const KNOWLEDGE_GRAPH = {
  company: {
    name: ORG.name,
    creciJuridico: ORG.creciJuridico,
    director: { name: DIRECTOR.name, creci: DIRECTOR.creci },
    city: CITY.name,
  },
  categories: PROPERTY_TYPES.map((p) => p.name),
  specialties: SPECIALTIES.map((s) => s.name),
  neighborhoods: NEIGHBORHOODS.map((n) => n.name),
  developers: DEVELOPERS.map((d) => d.name),
  developments: DEVELOPMENTS.map((d) => d.name),
};
